RivalIQ.apps.Insights.module('widgets', function (widgets, App, Backbone, Marionette, $, _) {

    'use strict';

    const RetentionBarChartView = RivalIQ.apps.Dashboard.widgets.StackedBar.extend({

        loadData: function () {
            const me = this;

            const metric = new RivalIQ.models.MetricDefinition(me.dashboardModel.metric);
            const toolTipMetric = new RivalIQ.models.MetricDefinition(_.defaults({
                property: 'pct_of_story_days'
            }, me.dashboardModel.metric));

            return $.when(metric.fetch(), toolTipMetric.fetch())
                .done(function (metric, toolTipMetric) {
                    me.collection = new RivalIQ.collections.MetricDefinitions([metric]);

                    me.toolTipMetricByIndex = _.reduce(toolTipMetric.getValue(), (memo, [storyIndex, data]) => {
                        memo[storyIndex] = data;
                        return memo;
                    }, {});
                });
        },

        getTooltip: function () {
            const me = this;

            return {

                title: function (data, index) {
                    const metric = me.collection.first();
                    return metric.formatDomainValue(metric.getValue({
                        format: false
                    })[index][0]);
                },

                content: function (data, index) {

                    const metric = me.collection.first();

                    const metricData = {
                        value: metric.format(data[0].value),
                        name: 'Retention Rate'
                    };

                    const numFrames = metric.getValue({
                        format: false
                    })[index][0];
                    const pctOfStoryDays = _.get(me.toolTipMetricByIndex, [numFrames]);

                    metricData.submetric = {
                        displayString: `${pctOfStoryDays} of stories have at least ${metric.formatDomainValue(numFrames)}`
                    };

                    return new App.views.ChartTooltip({
                        model: metricData
                    }).render().el.outerHTML;
                }
            };
        },

        getChartAttributes: function () {
            const me = this;

            const attrs = RivalIQ.apps.Dashboard.widgets.StackedBar.prototype.getChartAttributes.apply(me, arguments);

            // index the data by index in array instead of by id; we'll correctly convert the index to a readable string for ticks later in the metric.
            attrs.data[0] = _.map(attrs.data[0], function (d, idx) {
                return [idx, d[1]];
            });

            return _.extend(attrs, {
                concept: me.collection.first(),
                tickPosition: RivalIQ.enums.graph.position.MIDDLE
            });
        },

        getMetricClassNames: function () {

            const me = this;
            return me.collection.invoke('getClassName');
        },

        getChartClassName: function () {
            const me = this;
            return me.getMetricClassNames();
        }
    });

    const RetentionBarChartWidget = RivalIQ.apps.Dashboard.models.Widget.extend({

        view: RetentionBarChartView,

        processProps: function (props) {
            const me = this;

            _.defaults(props, {
                objectType: RivalIQ.enums.insightsObjectType.storyDay
            });

            if (!me.metric) {
                me.metric = App.utils.createInsightsMetric(props.objectType, _.defaults({
                    dimensions: ['timeAnalysisMode', 'channel', 'companyId', 'frameRetention'],
                    property: 'stories_retention_rate',
                    name: 'Retention Rate by Frame'
                }, props));
            }
            _.extend(me.metric, props);

            return props;
        },

        labels: {
            x: 'Frame order'
        },

        getMetricLabel: _.constant('Retention Rate by frame')
    });

    Marionette.Dashboard.widgets.add(RivalIQ.enums.widgetTypes.INSTAGRAM_INSIGHTS_STORIES_RETENTION_RATE_BAR_CHART, RetentionBarChartWidget);


    const RetentionView = RivalIQ.views.LayoutView.mixin({
        template: '#instagramStoriesRetentionRateTemplate',
        childView: RivalIQ.apps.Dashboard.views.Widget,

        mixins: {
            props: Marionette.Dashboard.mixins.Props
        },

        dashboardModelEvents: {
            'change': 'reload'
        },

        regions: {
            summaryMetric1: '.x-summary-metric1',
            summaryMetric2: '.x-summary-metric2',
            barChart: '.x-bar-chart'
        },

        ui: {
            dataArea: '.x-data',
            notApplicable: '.not-applicable-warning'
        },

        onBeforeRenderLoaded: function () {
            const me = this;

            me.summaryMetric1.showWidget(me.dashboardModel.getWidget('summary-metric1'));
            me.summaryMetric2.showWidget(me.dashboardModel.getWidget('summary-metric2'));
            me.barChart.showWidget(me.dashboardModel.getWidget('bar-chart'));
        },

        onRenderLoaded: function () {
            const me = this;

            if (_.isEmpty(me.getProp('searchTerm'))) {
                me.ui.dataArea.show();
                me.ui.notApplicable.hide();
            } else {
                me.ui.dataArea.hide();
                me.ui.notApplicable.show();
            }
        }
    });

    const RetentionWidget = RivalIQ.apps.Dashboard.models.Widget.extend({

        propsToSerializeForExport: [],

        getExportTitle: App.utils.insightsExportTitle,
        getExportSubtitle: App.utils.insightsExportSubtitle,
        getPptSubtitle: App.utils.insightsPptSubtitle,
        scheduledExportAdvancedSettingsEnabled: false,

        title: 'Story Retention Rate',
        description: RivalIQ.strings.instagram_insights_retention_rate_description,
        view: RetentionView,
        viewMore: false,

        processProps: function (props) {
            const me = this;

            props.submetricDisplayType = 'upDownCarets';

            _.defaults(props, {
                limit: 200
            });

            if (!me.metric) {
                me.metric = App.utils.createInsightsMetric(RivalIQ.enums.insightsObjectType.storyDay, _.defaults({
                    dimensions: ['timeAnalysisMode', 'channel', 'companyId']
                }, props));
            }
            _.extend(me.metric, props);

            return props;
        },

        widgetDefinitions: function () {
            const me = this;
            const props = me.getProps();

            const metric = new RivalIQ.models.MetricDefinition(me.metric);
            return [
                {
                    instanceId: 'summary-metric1',
                    widgetId: RivalIQ.enums.widgetTypes.METRIC,
                    metric: metric.build({
                        property: 'stories_retention_rate',
                        companyId: props.companyId
                    }),
                    submetric: metric.build({
                        property: 'stories_retention_rate',
                        timeAnalysisMode: RivalIQ.enums.timeAnalysisMode.DELTA_RELATIVE
                    }),
                    props: {
                        submetricType: 'delta_relative'
                    }
                },
                {
                    instanceId: 'summary-metric2',
                    widgetId: RivalIQ.enums.widgetTypes.METRIC,
                    metric: metric.build({
                        property: 'stories_per_day',
                        companyId: props.companyId
                    }),
                    submetric: metric.build({
                        property: 'stories_per_day',
                        timeAnalysisMode: RivalIQ.enums.timeAnalysisMode.DELTA_RELATIVE
                    }),
                    props: {
                        submetricType: 'delta_relative'
                    }
                },
                {
                    instanceId: 'bar-chart',
                    widgetId: RivalIQ.enums.widgetTypes.INSTAGRAM_INSIGHTS_STORIES_RETENTION_RATE_BAR_CHART
                }
            ];
        }
    });

    Marionette.Dashboard.widgets.add(RivalIQ.enums.widgetTypes.INSTAGRAM_INSIGHTS_STORIES_RETENTION_RATE, RetentionWidget);
});
